/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Friday, October 1st 2021, 5:50:58 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { LinearProgress } from "@mui/material";
import {
  OSCircularProgress,
  CircularProgressCentered,
  CircularProgressInFront,
  SkeletonProgessLoader,
  FormSkeletonProgessLoader,
  BackdropLoader,
} from "ui-widgets";
import { SkeletonProps } from "@mui/material/Skeleton";

/**
 * showLoader is a mandatory parameter for our ProgressWrapper so it can either render a loader or render
 * the children provided.
 *
 */
export interface ProgressWrapperProps extends React.HTMLProps<HTMLDivElement> {
  showLoader: boolean;
  loaderType?:
    | "circular"
    | "circularCentered"
    | "linear"
    | "circularInFront"
    | "skeleton"
    | "form-skeleton"
    | "custom"
    | "transparentBackdrop"
    | "none";
  customProgressLoader?: JSX.Element;
  skeletonProps?: SkeletonProps;
}

const ProgressWrapper = ({ showLoader, customProgressLoader = null, children, ...props }: ProgressWrapperProps) => {
  let loaderComponent: JSX.Element = <CircularProgressCentered />;

  switch (props.loaderType) {
    case "circular":
      loaderComponent = <OSCircularProgress />;
      break;
    case "linear":
      loaderComponent = <LinearProgress />;
      break;
    case "circularCentered":
      loaderComponent = <CircularProgressCentered />;
      break;
    case "circularInFront":
      loaderComponent = <CircularProgressInFront />;
      break;
    case "skeleton":
      loaderComponent = <SkeletonProgessLoader {...props.skeletonProps} />;
      break;
    case "form-skeleton":
      loaderComponent = <FormSkeletonProgessLoader {...props.skeletonProps} />;
      break;
    case "transparentBackdrop":
      loaderComponent = <BackdropLoader open={true} />;
      break;
    case "custom":
      loaderComponent = customProgressLoader;
      break;
    case "none":
      loaderComponent = null;
  }

  return showLoader && loaderComponent ? loaderComponent : <React.Fragment>{children}</React.Fragment>;
};

export default ProgressWrapper;
